import React from "react";
import { Sun, CloudSun, Cloud as CloudIcon, CloudRain, CloudSnow, CloudFog, CloudLightning, MapPin } from "lucide-react";
import BannerIcon from "./BannerIcon";

// Renders the body of a location quadrant (Production Office / Basecamp / Parking):
// - If the value contains a newline, first line is bold TITLE, remainder is address block.
// - Address block is wrapped in a Google Maps link and preserves line breaks.
function renderQuadrantBody(value, mapLinkStyle) {
  const v = (value || "").trim();
  if (!v) return null;
  const lines = v.split("\n");
  const first = lines[0].trim();
  const rest = lines.slice(1).map(l => l).filter(l => l.trim());
  const addr = rest.join("\n").trim();
  if (addr) {
    return (
      <>
        <div style={{ fontSize: "6.1pt", fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>{first}</div>
        <div style={{ fontSize: "6.1pt", whiteSpace: "pre-line", textAlign: "center", lineHeight: 1.2 }}>
          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addr)}`}
             target="_blank" rel="noreferrer" style={mapLinkStyle}>
            {addr}
          </a>
        </div>
      </>
    );
  }
  return (
    <div style={{ fontSize: "6.1pt", whiteSpace: "pre-line", textAlign: "center", lineHeight: 1.2 }}>
      <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(first)}`}
         target="_blank" rel="noreferrer" style={mapLinkStyle}>
        {first}
      </a>
    </div>
  );
}

// Small inline weather glyph for the WEATHER card
function WeatherGlyph({ icon, size = 40 }) {  const c = { color: "#334155" };
  const p = { size, strokeWidth: 2.2 };
  switch (icon) {
    case "clear": return <Sun {...p} style={{ color: "#f59e0b" }} />;
    case "partly_cloudy": return <CloudSun {...p} style={{ color: "#64748b" }} />;
    case "cloudy": return <CloudIcon {...p} style={c} />;
    case "fog": return <CloudFog {...p} style={c} />;
    case "rain":
    case "heavy_rain": return <CloudRain {...p} style={{ color: "#3b82f6" }} />;
    case "snow": return <CloudSnow {...p} style={{ color: "#93c5fd" }} />;
    case "thunderstorm": return <CloudLightning {...p} style={{ color: "#8b5cf6" }} />;
    default: return <CloudIcon {...p} style={c} />;
  }
}

// Time / pages helpers (mirror PDF generator formatting)
export const parseDurMin = (s) => {
  if (!s) return 0; s = String(s).trim().toLowerCase(); if (!s) return 0;
  if (s.includes(":")) { const [h, m] = s.split(":"); return (+h || 0) * 60 + (+m || 0); }
  let t = 0; const hm = s.match(/(\d+)\s*h/); const mm = s.match(/(\d+)\s*m(?!i?d)/);
  if (hm) t += parseInt(hm[1], 10) * 60; if (mm) t += parseInt(mm[1], 10);
  return t || (/^\d+$/.test(s) ? parseInt(s, 10) : 0);
};
export const fmtMin = (m) => { if (m <= 0) return "0m"; const h = Math.floor(m / 60), r = m % 60; if (h && r) return `${h}h ${r}m`; return h ? `${h}h` : `${r}m`; };
export const parsePE = (s) => {
  if (!s) return 0; s = String(s).trim();
  const mx = s.match(/^\s*(?:(\d+)\s+)?(\d+)\s*\/\s*(\d+)\s*$/);
  if (mx) { const w = parseInt(mx[1] || "0", 10), n = parseInt(mx[2], 10), d = parseInt(mx[3] || "1", 10); if (d === 0) return w * 8; return w * 8 + Math.round(n * 8 / d); }
  const dc = s.match(/^\s*(\d+)(?:\.(\d+))?\s*$/);
  if (dc) { const w = parseInt(dc[1], 10); const f = dc[2] ? parseFloat("0." + dc[2]) : 0; return w * 8 + Math.round(f * 8); }
  return 0;
};
export const fmtPg = (e) => {
  if (e <= 0) return "0"; const w = Math.floor(e / 8), r = e % 8;
  if (r === 0) return String(w); const gcd = (a, b) => b === 0 ? a : gcd(b, a % b); const g = gcd(r, 8); const n = r / g, d = 8 / g;
  return w ? `${w} ${n}/${d}` : `${n}/${d}`;
};
export const addOff = (base, off) => {
  if (!base || !off) return ""; const m = String(base).match(/^\s*(\d{1,2}):(\d{2})\s*$/);
  if (!m) return ""; const b = (+m[1]) * 60 + (+m[2]); let o = off.trim().toLowerCase(); const sign = o.startsWith("-") ? -1 : 1;
  o = o.replace(/^[+-]/, "").trim(); const d = parseDurMin(o); if (d === 0 && !o) return "";
  let t = (b + sign * d) % (24 * 60); if (t < 0) t += 24 * 60; return `${String(Math.floor(t / 60)).padStart(2, "0")}:${String(t % 60).padStart(2, "0")}`;
};

function SectionHeader({ children, accent }) {
  return (
    <div style={{ background: accent || "#1a1a1a", color: "white", padding: "2.1pt 5.8pt", fontWeight: "bold", fontSize: "6.4pt", letterSpacing: "0.7pt", textTransform: "uppercase", marginTop: "4.4pt" }}>
      {children}
    </div>
  );
}

/**
 * Full HTML mirror of the PDF call sheet.
 * Props:
 *   cs            – the call sheet form/document
 *   accentColor   – optional hex string (project accent) used for banners
 *   logoDataUri   – optional data URI for production logo
 *   isDraft       – renders the diagonal PRELIM watermark overlay
 */
export default function CallSheetHtmlPreview({ cs, accentColor, logoDataUri, isDraft, crewDb, deptOrder, logoAlign, logoMaxHeightPt, printMode = false }) {
  const w = cs?.weather || {};
  const h = cs?.nearest_hospital || {};
  const scenes = cs?.scenes || [];
  const clientRows = (cs?.clients || []).filter((row) => Object.values(row || {}).some(Boolean));
  const hiddenClientColumns = new Set(cs?.hidden_client_columns || []);
  const clientColumns = [
    { key: "name", header: "CLIENT" },
    { key: "title", header: "TITLE" },
    ...(!hiddenClientColumns.has("phone") && clientRows.some((client) => client.phone) ? [{ key: "phone", header: "PHONE" }] : []),
    ...(!hiddenClientColumns.has("email") && clientRows.some((client) => client.email) ? [{ key: "email", header: "EMAIL" }] : []),
    { key: "call_time", header: "CALL" },
    ...(!hiddenClientColumns.has("notes") && clientRows.some((client) => client.notes) ? [{ key: "notes", header: "NOTES" }] : []),
  ];
  const standInsBackgroundRows = (cs?.stand_ins_background || []).filter((row) => (
    Object.values(row || {}).some(Boolean)
  ));
  const standInRows = standInsBackgroundRows.filter((row) => row.type !== "background");
  const backgroundRows = standInsBackgroundRows.filter((row) => row.type === "background");
  // Build id → crew record lookup so we can backfill phone/email on rows saved before those fields existed
  const _crewById = React.useMemo(() => {
    const m = {};
    (crewDb || []).forEach(c => { if (c && c.id) m[c.id] = c; });
    return m;
  }, [crewDb]);
  const _enrich = (row) => {
    if (!row || !row.crew_id) return row;
    const src = _crewById[row.crew_id];
    if (!src) return row;
    return {
      ...row,
      phone: row.phone || src.phone || "",
      email: row.email || src.email || "",
    };
  };
  const cast = (cs?.cast_calls || []).map(_enrich);
  const depts = cs?.dept_calls || [];
  // Sort crew by (dept-index in project department order, then sort_order, then name)
  // so the preview and PDF match the Crew Assignments editor exactly.
  const _deptIdx = React.useMemo(() => {
    const list = deptOrder || cs?._department_order || [];
    return Object.fromEntries(list.map((d, i) => [d, i]));
  }, [deptOrder, cs?._department_order]);
  const crew = React.useMemo(() => {
    const rows = (cs?.crew_assignments || []).map(_enrich);
    return rows.slice().sort((a, b) => {
      const ad = a?.department || "";
      const bd = b?.department || "";
      const ai = _deptIdx[ad] ?? 999;
      const bi = _deptIdx[bd] ?? 999;
      if (ai !== bi) return ai - bi;
      // Same known-index OR both unknown (999) → fall back to dept name so rows still cluster
      if (ad !== bd) return ad.localeCompare(bd);
      const so = (a?.sort_order ?? 0) - (b?.sort_order ?? 0);
      if (so) return so;
      return (a?.name || "").localeCompare(b?.name || "");
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cs?.crew_assignments, _crewById, _deptIdx]);

  const accent = accentColor || "#1a1a1a";
  const borderRule = (color = "#000") => `0.2pt solid ${color}`;
  const tdStyle = {
    border: borderRule(),
    padding: "1.4pt 3.6pt",
    fontSize: "6.4pt",
    verticalAlign: "top",
    color: "#000",
  };
  const thStyle = { ...tdStyle, background: "#d0d0d0", fontWeight: "bold" };
  const labelStyle = { fontSize: "5.0pt", fontWeight: "bold", color: "#666", letterSpacing: "0.7pt", marginBottom: "2.1pt" };
  const mapLinkStyle = { color: "inherit", textDecoration: "none" };
  const DEFAULT_FOOTER_LEFT = "CONFIDENTIAL – NOT FOR DISTRIBUTION";
  const footerLeft = (cs?.footer_left_text || "").trim() || DEFAULT_FOOTER_LEFT;
  const FooterStrip = () => (
    <div style={{ marginTop: "8.6pt", borderTop: borderRule("#333"), paddingTop: "4.4pt", display: "flex", justifyContent: "space-between", fontSize: "5.8pt", color: "#555" }}>
      <span><b>{footerLeft}</b></span>
      <span>Generated by Get2Set</span>
    </div>
  );
  // Make each page card a flex column so the footer sticks to the bottom of the 8.5x11 card.
  // In printMode the page becomes an exact 11in tall block with no drop shadow (Chromium then
  // page-breaks cleanly between .page divs — no phantom slivers on page 2/3).
  const pageStyle = printMode
    ? { position: "relative", background: "white", color: "black", fontFamily: "Arial, Helvetica, sans-serif",
        padding: "20.2pt", height: "11in", width: "8.5in", boxSizing: "border-box",
        display: "flex", flexDirection: "column", overflow: "hidden" }
    : { position: "relative", background: "white", color: "black", fontFamily: "Arial, Helvetica, sans-serif",
        padding: "20.2pt", minHeight: "11in", width: "8.5in", boxSizing: "border-box",
        boxShadow: "0 2.8pt 11.4pt rgba(0,0,0,0.25)", display: "flex", flexDirection: "column" };
  const pageBodyStyle = { flex: "1 1 auto" };
  const DraftWatermark = () => isDraft ? (
    <div aria-hidden style={{
      position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center",
      pointerEvents: "none", zIndex: 5, overflow: "hidden",
    }}>
      <div style={{
        transform: "rotate(-30deg)", fontSize: "100.4pt", fontWeight: 900,
        color: "rgba(217,38,38,0.16)", letterSpacing: "5.8pt",
      }}>PRELIM</div>
    </div>
  ) : null;

  return (
    <div style={printMode
      ? { display: "flex", flexDirection: "column", background: "white" }
      : { display: "flex", flexDirection: "column", alignItems: "center", gap: "17.2pt", padding: "11.4pt 0", background: "transparent" }}>
      {/* ─────────── PAGE 1 ─────────── */}
      <div style={pageStyle} data-print-page="1">
        <DraftWatermark />
        <div style={pageBodyStyle}>

      {/* HEADER ROW 1: Logo+Name (left, centered) | CREW CALL (center, vcentered) | DATE/DAY (right) */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), position: "relative", zIndex: 1 }}>
        <tbody>
          <tr>
            <td style={{ ...tdStyle, width: "40%", padding: "8.6pt 7.2pt", textAlign: (["left", "center", "right"].includes(cs?.logo_align || logoAlign) ? (cs?.logo_align || logoAlign) : "center"), verticalAlign: "middle" }}>
              {logoDataUri ? (
                <>
                  <img src={logoDataUri} alt="Logo" style={{ maxHeight: `${Math.max(10, Math.min(72, Number(cs?.logo_max_height_pt) || Number(logoMaxHeightPt) || 36))}pt`, maxWidth: "80%", objectFit: "contain", display: "inline-block" }} />
                  <div style={{ fontSize: "9.3pt", fontWeight: 900, letterSpacing: "0.5pt", marginTop: "4.4pt" }}>
                    {cs?.production_name || "PRODUCTION NAME"}
                  </div>
                </>
              ) : (
                <div style={{ fontSize: "10.8pt", fontWeight: 900, letterSpacing: "0.5pt" }}>{cs?.production_name || "PRODUCTION NAME"}</div>
              )}
            </td>
            <td style={{ ...tdStyle, background: accent, color: "white", textAlign: "center", verticalAlign: "middle", width: "20%", padding: "5.8pt 2.8pt", lineHeight: 1.1 }}>
              <div style={{ fontSize: "5.8pt", fontWeight: "bold", letterSpacing: "1.4pt", opacity: 0.9 }}>CREW CALL</div>
              <div style={{ fontSize: "20.1pt", fontWeight: 900, letterSpacing: "0.5pt", marginTop: "2.7pt" }}>
                {cs?.general_call?.trim() || "—"}
              </div>
              <div style={{ fontSize: "5.8pt", fontWeight: "bold", opacity: 0.9, marginTop: "2.1pt", lineHeight: 1.25 }}>CHECK INDIVIDUAL<br/>CALL TIMES</div>
            </td>
            <td style={{ ...tdStyle, width: "40%", padding: "8.6pt 7.2pt", verticalAlign: "middle", textAlign: "center" }}>
              {(() => {
                const raw = (cs?.shoot_date || "").slice(0, 10);
                let longDate = raw || "—";
                if (raw && /^\d{4}-\d{2}-\d{2}$/.test(raw)) {
                  const [y, m, d] = raw.split("-").map(Number);
                  const local = new Date(y, m - 1, d);
                  if (!isNaN(local.getTime())) {
                    longDate = local.toLocaleDateString(undefined, { weekday: "long", year: "numeric", month: "long", day: "numeric" });
                  }
                }
                let dayLine = (cs?.day_of_days || "").trim();
                if (dayLine && !/^day\b/i.test(dayLine)) dayLine = `Day ${dayLine}`;
                return (
                  <>
                    <div style={{ fontSize: "7.2pt", fontWeight: 700, letterSpacing: "0.3pt" }}>{longDate}</div>
                    <div style={{ fontSize: "14.4pt", fontWeight: 900, letterSpacing: "0.5pt", marginTop: "2.8pt", lineHeight: 1.1 }}>{dayLine || "—"}</div>
                  </>
                );
              })()}
            </td>
          </tr>
        </tbody>
      </table>

      {/* INFO ROW: Key Contacts | 2x2 quadrant | Weather (2/7 · 3/7 · 2/7) */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), borderTop: "none" }}>
        <tbody>
          <tr>
            {/* LEFT: KEY CONTACTS */}
            <td style={{ ...tdStyle, width: "28.5714%", padding: "5.8pt 7.2pt", verticalAlign: "top" }}>
              <div style={labelStyle}>KEY CONTACTS</div>
              {((cs?.key_contacts || []).length > 0) ? (
                (cs.key_contacts || []).map((kc, i) => (
                  <div key={i} style={{ fontSize: "6.1pt", marginBottom: "0.7pt" }}>
                    <b>{kc.role || "Contact"}:</b> {kc.name}
                  </div>
                ))
              ) : (
                <>
                  {cs?.director && <div style={{ fontSize: "6.1pt", marginBottom: "0.7pt" }}><b>DIRECTOR:</b> {cs.director}</div>}
                  {cs?.producer && <div style={{ fontSize: "6.1pt", marginBottom: "0.7pt" }}><b>PRODUCER:</b> {cs.producer}</div>}
                  {cs?.first_ad && <div style={{ fontSize: "6.1pt", marginBottom: "0.7pt" }}><b>1ST AD:</b> {cs.first_ad}</div>}
                  {cs?.unit_pm && <div style={{ fontSize: "6.1pt" }}><b>UPM:</b> {cs.unit_pm}</div>}
                </>
              )}
            </td>

            {/* MIDDLE: 2x2 quadrant (with center map-pin badge at intersection) */}
            <td
              data-testid="location-quadrants"
              style={{
                ...tdStyle,
                width: "42.8571%",
                padding: 0,
                position: "relative",
                // `height: 1px` is a CSS trick that lets inner `height: 100%` resolve to the
                // row's natural height (otherwise table cells auto-size to content and the
                // inner table never stretches to the row's bottom border).
                height: "1px",
              }}
            >
              <table
                data-testid="location-quadrant-grid"
                style={{ width: "100%", height: "100%", borderCollapse: "collapse" }}
              >
                <tbody>
                  <tr>
                    <td style={{ ...tdStyle, width: "50%", padding: "4.4pt 5.8pt", verticalAlign: "middle", textAlign: "center", borderRight: borderRule(), borderBottom: borderRule() }}>
                      <div style={{ ...labelStyle, textAlign: "center" }}>PRODUCTION OFFICE</div>
                      {renderQuadrantBody(cs?.production_office_address, mapLinkStyle)}
                    </td>
                    <td style={{ ...tdStyle, width: "50%", padding: "4.4pt 5.8pt", verticalAlign: "middle", textAlign: "center", borderBottom: borderRule() }}>
                      <div style={{ ...labelStyle, textAlign: "center" }}>NEAREST HOSPITAL</div>
                      {h.name && <div style={{ fontSize: "6.1pt", fontWeight: 700, textAlign: "center", lineHeight: 1.2 }}>{h.name}</div>}
                      {h.address && (
                        <div style={{ fontSize: "6.1pt", textAlign: "center", whiteSpace: "pre-line", lineHeight: 1.2 }}>
                          <a href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(h.address)}`}
                             target="_blank" rel="noreferrer" style={mapLinkStyle}>
                            {h.address}
                          </a>
                        </div>
                      )}
                      {h.phone && <div style={{ fontSize: "6.1pt", textAlign: "center", lineHeight: 1.2 }}>Tel: {h.phone}</div>}
                    </td>
                  </tr>
                  <tr>
                    <td style={{ ...tdStyle, width: "50%", padding: "4.4pt 5.8pt", verticalAlign: "middle", textAlign: "center", borderRight: borderRule() }}>
                      <div style={{ ...labelStyle, textAlign: "center" }}>BASECAMP</div>
                      {renderQuadrantBody(cs?.location_address || cs?.location_name, mapLinkStyle)}
                    </td>
                    <td style={{ ...tdStyle, width: "50%", padding: "4.4pt 5.8pt", verticalAlign: "middle", textAlign: "center" }}>
                      <div style={{ ...labelStyle, textAlign: "center" }}>PARKING</div>
                      {renderQuadrantBody(cs?.parking_address, mapLinkStyle)}
                    </td>
                  </tr>
                </tbody>
              </table>
              {/* Centered pin-in-circle sitting exactly at the intersection of the 4 quadrants */}
              <div style={{
                position: "absolute", top: "50%", left: "50%",
                transform: "translate(-50%, -50%)",
                width: 29, height: 29, borderRadius: "50%",
                background: "#fff", border: borderRule(),
                display: "flex", alignItems: "center", justifyContent: "center",
                pointerEvents: "none", zIndex: 2,
                boxShadow: "0 0 0 0.2pt #fff",
              }}>
                <MapPin size={16} color="#111" strokeWidth={2.2} />
              </div>
            </td>

            {/* RIGHT: WEATHER */}
            <td style={{ ...tdStyle, width: "28.5714%", padding: "5.8pt 7.2pt", verticalAlign: "top" }}>
              <div style={labelStyle}>WEATHER</div>
              {(w.temp_high || w.temp_low || w.description || w.conditions || w.temperature) ? (
                <div>
                  {/* Top row: LOW  |  ICON  |  HIGH */}
                  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-around", padding: "1.4pt 1.4pt 2.8pt", gap: "4.4pt" }}>
                    <div style={{ textAlign: "center", flex: "0 0 auto" }}>
                      <div style={{ fontSize: "12.2pt", fontWeight: 400, lineHeight: 1 }}>{w.temp_low || "—"}</div>
                      <div style={{ fontSize: "5.8pt", color: "#4b5563", letterSpacing: "0.4pt" }}>low</div>
                    </div>
                    <div style={{ flex: "0 0 auto" }}>
                      <WeatherGlyph icon={w.icon} size={40} />
                    </div>
                    <div style={{ textAlign: "center", flex: "0 0 auto" }}>
                      <div style={{ fontSize: "12.2pt", fontWeight: 400, lineHeight: 1 }}>{w.temp_high || "—"}</div>
                      <div style={{ fontSize: "5.8pt", color: "#4b5563", letterSpacing: "0.4pt" }}>high</div>
                    </div>
                  </div>
                  {/* Description */}
                  {(w.description || w.conditions || w.temperature) && (
                    <div style={{ fontSize: "5.3pt", textAlign: "center", padding: "1.4pt 2.8pt 2.8pt", lineHeight: 1.35, color: "#111827" }}>
                      {w.description || [w.conditions, w.temperature].filter(Boolean).join(" · ")}
                    </div>
                  )}
                  {/* Sunrise / Sunset */}
                  {(w.sunrise || w.sunset) && (
                    <div style={{ display: "flex", justifyContent: "space-between", borderTop: borderRule(), paddingTop: "2.1pt", fontSize: "6.1pt" }}>
                      <div><b>Sunrise:</b> {w.sunrise || ""}</div>
                      <div><b>Sunset:</b> {w.sunset || ""}</div>
                    </div>
                  )}
                </div>
              ) : null}
            </td>
          </tr>
        </tbody>
      </table>

      {/* Standalone BASECAMP row removed — now shown as a quadrant inside the info row above */}

      {/* NOTES — full-width row directly under the info block */}
      <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), borderTop: "none" }}>
        <tbody>
          <tr>
            <td style={{ ...tdStyle, padding: "5.8pt 7.2pt", verticalAlign: "top" }}>
              <div style={labelStyle}>NOTES</div>
              <div style={{ fontSize: "6.4pt", whiteSpace: "pre-wrap", minHeight: "12.9pt" }}>
                {cs?.general_notes || ""}
              </div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* PRODUCTION REMINDERS — borderless strip between Notes and Today's Schedule */}
      {(cs?.production_reminders || []).length > 0 && (
        <div style={{ padding: "4.4pt 7.2pt", textAlign: "center", fontSize: "5.3pt", fontWeight: 700, letterSpacing: "0.3pt", lineHeight: 1.35 }}>
          {(cs.production_reminders || []).map(r => (r || "").toUpperCase()).join("  |  ")}
        </div>
      )}

      {/* SCENES / TODAY'S SCHEDULE — TIME-first */}
      {scenes.length > 0 && (() => {
        // Column schema — filter by hidden_schedule_columns; SET/DESCRIPTION is
        // controlled by two editor keys (set_name + description) and is only
        // hidden when BOTH are hidden.
        const hiddenSet = new Set(cs?.hidden_schedule_columns || []);
        const ALL_COLS = [
          { key: "time", label: "TIME", weight: 0.067 },
          { key: "scene_num", label: "SC#", weight: 0.042 },
          { key: "int_ext", label: "I/E", weight: 0.059 },
          { key: "day_night", label: "D/N", weight: 0.067 },
          { key: "set_desc", label: "SET / DESCRIPTION", weight: 0.420 },
          { key: "cast_ids", label: "CAST", weight: 0.085 },
          { key: "pages", label: "PGS", weight: 0.050 },
          { key: "location", label: "LOCATION", weight: 0.210 },
        ];
        const colHidden = (k) => k === "set_desc"
          ? (hiddenSet.has("set_name") && hiddenSet.has("description"))
          : hiddenSet.has(k);
        const visibleCols = ALL_COLS.filter(c => !colHidden(c.key));
        const colIdx = (k) => visibleCols.findIndex(c => c.key === k);
        // Compute vertical merge groups for the LOCATION column.
        const locOf = (row) => (row.type || "scene") === "banner"
          ? (row.banner_location || "").trim()
          : (row.location || "").trim();
        const groupStart = new Array(scenes.length).fill(0);
        const groupSize = new Array(scenes.length).fill(1);
        const groupHasBanner = new Array(scenes.length).fill(false);
        const groupHasScene = new Array(scenes.length).fill(false);
        let i = 0;
        while (i < scenes.length) {
          let j = i;
          const cur = locOf(scenes[i]);
          if (cur) {
            while (j + 1 < scenes.length && locOf(scenes[j + 1]) === cur) j++;
          }
          for (let k = i; k <= j; k++) {
            groupStart[k] = i;
            groupSize[k] = (j - i + 1);
          }
          for (let k = i; k <= j; k++) {
            const isB = (scenes[k].type || "scene") === "banner";
            if (isB) groupHasBanner[i] = true; else groupHasScene[i] = true;
          }
          i = j + 1;
        }
        // Render the multi-line location value. First line is treated as a
        // title (bold) when the value has more than one line; otherwise the
        // single line renders plain.
        const renderLoc = (text) => {
          const raw = String(text || "");
          const lines = raw.split("\n");
          if (lines.length === 0 || (lines.length === 1 && !lines[0])) return null;
          if (lines.length > 1) {
            const [head, ...rest] = lines;
            return (
              <>
                <div style={{ fontWeight: "bold" }}>{head}</div>
                {rest.map((line, idx) => <div key={idx}>{line}</div>)}
              </>
            );
          }
          return <span>{lines[0]}</span>;
        };
        return (
        <>
          <SectionHeader accent={accent}>Today's Schedule</SectionHeader>
          <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), tableLayout: "fixed" }}>
            <colgroup>
              {(() => {
                const total = visibleCols.reduce((a, c) => a + c.weight, 0) || 1;
                return visibleCols.map(c => (
                  <col key={c.key} style={{ width: `${(c.weight / total * 100).toFixed(2)}%` }} />
                ));
              })()}
            </colgroup>
            <thead>
              <tr>{visibleCols.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}</tr>
            </thead>
            <tbody>
              {scenes.map((s, i) => {
                if ((s.type || "scene") === "banner") {
                  const t = (s.banner_time || addOff(cs?.general_call, s.banner_offset || ""));
                  const icon = (s.banner_icon || "").trim();
                  const loc = (s.banner_location || "").trim();
                  const label = (s.banner_label || s.description || "");
                  const isGroupStart = groupStart[i] === i;
                  const rowSpan = groupSize[i];
                  const timeVisible = colIdx("time") >= 0;
                  const locVisible = colIdx("location") >= 0;
                  const hasTime = !!(t && String(t).trim()) && timeVisible;
                  const hasLoc = !!loc && locVisible;
                  const covered = !isGroupStart;
                  const totalVisible = visibleCols.length;
                  // Label spans everything between (visible) time & (visible) location cells.
                  const timeCount = timeVisible ? 1 : 0;
                  const locCount = locVisible ? 1 : 0;
                  let labelColSpan = totalVisible - timeCount - locCount;
                  if (!hasTime && timeVisible) labelColSpan += 1; // time col shown but empty → absorb
                  if (!hasLoc && locVisible && !covered) labelColSpan += 1;
                  const timeCellOn = timeVisible && hasTime;
                  const locCellOn = locVisible && hasLoc && isGroupStart;
                  return (
                    <tr key={i} style={{ background: "#e5e7eb" }}>
                      {timeCellOn && (
                        <td style={{ ...tdStyle, fontWeight: 700, textAlign: "center", verticalAlign: "middle" }}>{t}</td>
                      )}
                      <td colSpan={labelColSpan} style={{ ...tdStyle, textAlign: "center", verticalAlign: "middle", fontWeight: "bold", padding: "4.4pt" }}>
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "2.8pt", verticalAlign: "middle" }}>
                          {icon && <BannerIcon slug={icon} size={16} color="black" />}
                          <span>{label}</span>
                        </span>
                      </td>
                      {locCellOn && (() => {
                        const gs = groupStart[i];
                        const mixed = groupHasBanner[gs] && groupHasScene[gs];
                        const bgStyle = mixed ? { background: "white" } : {};
                        return (
                          <td rowSpan={rowSpan} style={{ ...tdStyle, ...bgStyle, verticalAlign: "middle", textAlign: "center", whiteSpace: "pre-line" }}>{renderLoc(loc)}</td>
                        );
                      })()}
                    </tr>
                  );
                }
                const sceneLoc = (s.location || "").trim();
                const isGroupStartScene = groupStart[i] === i;
                const rowSpanScene = groupSize[i];
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "white" }}>
                    {visibleCols.map(c => {
                      const centerStyle = c.key === "set_desc"
                        ? { ...tdStyle, verticalAlign: "middle" }
                        : { ...tdStyle, verticalAlign: "middle", textAlign: "center" };
                      if (c.key === "time") return <td key="time" style={{ ...centerStyle, fontWeight: 700 }}>{s.time || ""}</td>;
                      if (c.key === "scene_num") return <td key="scene_num" style={centerStyle}>{s.scene_num}</td>;
                      if (c.key === "int_ext") return <td key="int_ext" style={centerStyle}>{s.int_ext}</td>;
                      if (c.key === "day_night") return <td key="day_night" style={centerStyle}>{s.day_night}</td>;
                      if (c.key === "set_desc") {
                        const showSet = !hiddenSet.has("set_name");
                        const showDesc = !hiddenSet.has("description");
                        return (
                          <td key="set_desc" style={centerStyle}>
                            {showSet && s.set_name && <div style={{ fontWeight: 700 }}>{s.set_name}</div>}
                            {showDesc && s.description}
                          </td>
                        );
                      }
                      if (c.key === "cast_ids") return <td key="cast_ids" style={centerStyle}>{s.cast_ids}</td>;
                      if (c.key === "pages") return <td key="pages" style={centerStyle}>{s.pages}</td>;
                      if (c.key === "location") {
                        if (!isGroupStartScene) return null;
                        return <td key="location" rowSpan={rowSpanScene} style={{ ...centerStyle, whiteSpace: "pre-line" }}>{renderLoc(sceneLoc)}</td>;
                      }
                      return null;
                    })}
                  </tr>
                );
              })}
              {scenes.some(s => (s.type || "scene") === "scene") && (() => {
                const pagesIdx = colIdx("pages");
                const locIdx = colIdx("location");
                const totalPages = fmtPg(scenes.reduce((a, s) => (s.type || "scene") === "scene" ? a + parsePE(s.pages) : a, 0));
                if (pagesIdx < 0) return null;
                return (
                  <tr style={{ background: "#f3f4f6", borderTop: borderRule() }}>
                    {pagesIdx > 0 && (
                      <td colSpan={pagesIdx} style={{ ...tdStyle, textAlign: "right", fontWeight: "bold" }}>TOTAL PAGES</td>
                    )}
                    <td style={{ ...tdStyle, fontWeight: "bold", textAlign: "center" }}>{totalPages}</td>
                    {locIdx > pagesIdx && <td style={tdStyle}></td>}
                  </tr>
                );
              })()}
            </tbody>
          </table>
        </>
        );
      })()}

      {/* TALENT (cast) */}
      {cast.length > 0 && (() => {
        const hiddenCast = new Set(cs?.hidden_cast_columns || []);
        const showEmail = !!cs?.show_cast_email;
        const showPhone = !!cs?.show_cast_phone;
        const ALL_CAST_COLS = [
          { key: "cast_id",   label: "#",         w: 5,  center: false },
          { key: "character", label: "CHARACTER", w: 15, center: false },
          { key: "actor",     label: "TALENT",    w: 19, center: false },
          { key: "status",    label: "STATUS",    w: 8,  center: false, bold: true },
          { key: "pickup",    label: "PICKUP",    w: 9,  center: true },
          { key: "arrive",    label: "ARRIVE",    w: 9,  center: true },
          { key: "hmua",      label: "H/MU",      w: 9,  center: true },
          { key: "onset",     label: "ON SET",    w: 9,  center: true, bold: true },
          { key: "phone",     label: "PHONE",     w: 13, center: false },
          { key: "email",     label: "EMAIL",     w: 18, center: false },
          { key: "notes",     label: "NOTES",     w: 17, center: false },
        ];
        const toggleMap = { pickup: "pickup_time", arrive: "arrive_time", hmua: "hmua_call", notes: "notes" };
        const visible = ALL_CAST_COLS.filter(c => {
          if (c.key === "phone") return showPhone;
          if (c.key === "email") return showEmail;
          const flag = toggleMap[c.key];
          if (flag && hiddenCast.has(flag)) return false;
          return true;
        });
        const totalW = visible.reduce((a, c) => a + c.w, 0) || 1;
        const val = (c, key) => ({
          cast_id: c.cast_id, character: c.character, actor: c.actor, status: c.status,
          pickup: c.pickup_time, arrive: c.arrive_time, hmua: c.hmua_call, onset: c.onset_call,
          phone: c.phone, email: c.email, notes: c.notes,
        }[key] || "");
        return (
          <>
            <SectionHeader accent={accent}>Talent</SectionHeader>
            <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule() }}>
              <colgroup>
                {visible.map(c => <col key={c.key} style={{ width: `${(c.w / totalW) * 100}%` }} />)}
              </colgroup>
              <thead>
                <tr>{visible.map(c => <th key={c.key} style={thStyle}>{c.label}</th>)}</tr>
              </thead>
              <tbody>
                {cast.map((c, i) => (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "white" }}>
                    {visible.map(col => (
                      <td key={col.key} style={{
                        ...tdStyle,
                        fontWeight: col.bold ? "bold" : undefined,
                        textAlign: col.center ? "center" : undefined,
                        verticalAlign: col.center ? "middle" : undefined,
                      }}>{val(c, col.key)}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        );
      })()}

      {/* STAND INS & BACKGROUND / CLIENTS — paired below Talent */}
      {(standInsBackgroundRows.length > 0 || clientRows.length > 0) && (
        <div
          data-testid="callsheet-supporting-cast-row"
          style={{ display: "flex", gap: "4.4pt", marginTop: "4.4pt", alignItems: "flex-start" }}
        >
          {standInsBackgroundRows.length > 0 && (
            <div data-testid="standins-background-print-panel" style={{ width: "50%", minWidth: 0 }}>
              <SectionHeader accent={accent}>Stand Ins &amp; Background</SectionHeader>
              {[
                { label: "STAND-INS", rows: standInRows },
                { label: "BACKGROUND", rows: backgroundRows },
              ].filter((group) => group.rows.length > 0).map((group, groupIndex) => (
                <table
                  key={group.label}
                  style={{
                    width: "100%",
                    borderCollapse: "collapse",
                    border: borderRule(),
                    borderTop: groupIndex > 0 ? "none" : borderRule(),
                    tableLayout: "fixed",
                  }}
                >
                  <colgroup>
                    <col style={{ width: "14%" }} />
                    <col style={{ width: "52%" }} />
                    <col style={{ width: "17%" }} />
                    <col style={{ width: "17%" }} />
                  </colgroup>
                  <thead>
                    <tr>{["QTY", group.label, "ARRIVE", "RDY"].map((header) => (
                      <th key={header} style={thStyle}>{header}</th>
                    ))}</tr>
                  </thead>
                  <tbody>
                    {group.rows.map((row, index) => (
                      <tr key={index} style={{ background: index % 2 === 0 ? "#fafafa" : "white" }}>
                        <td style={{ ...tdStyle, textAlign: "center", verticalAlign: "middle", fontWeight: 700 }}>{row.qty}</td>
                        <td style={tdStyle}>{row.description}</td>
                        <td style={{ ...tdStyle, textAlign: "center", verticalAlign: "middle", fontWeight: 700 }}>{row.arrive}</td>
                        <td style={{ ...tdStyle, textAlign: "center", verticalAlign: "middle" }}>{row.rdy}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ))}
            </div>
          )}
          {clientRows.length > 0 && (
            <div
              data-testid="clients-print-panel"
              style={{ width: standInsBackgroundRows.length > 0 ? "50%" : "100%", minWidth: 0 }}
            >
              <SectionHeader accent={accent}>Clients</SectionHeader>
              <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), tableLayout: "fixed" }}>
                <thead>
                  <tr>{clientColumns.map((column) => (
                    <th key={column.key} style={thStyle}>{column.header}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {clientRows.map((client, index) => (
                    <tr key={index} style={{ background: index % 2 === 0 ? "#fafafa" : "white" }}>
                      {clientColumns.map((column) => (
                        <td
                          key={column.key}
                          style={column.key === "call_time"
                            ? { ...tdStyle, fontWeight: 700, textAlign: "center", verticalAlign: "middle" }
                            : tdStyle}
                        >
                          {client[column.key]}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ─────────── end page 1 / start page 2 ─────────── */}
        </div>
        <FooterStrip />
      </div>
      {(crew.length > 0 || cs?.safety_notes || cs?.covid_protocols || cs?.walkie_channels || cs?.quote_of_the_day) && (
        <div style={pageStyle} data-print-page="2">
          <DraftWatermark />
          <div style={pageBodyStyle}>

      {/* CREW / DEPT — three master side-by-side columns */}
      {crew.length > 0 && (() => {
        const showEmail = !!cs?.show_crew_email;
        const showPhone = !!cs?.show_crew_phone;
        // Group crew by department (order already resolved upstream)
        const groups = [];
        let cur = null;
        crew.forEach(c => {
          const d = (c.department || "").trim() || "—";
          if (!cur || cur.dept !== d) { cur = { dept: d, members: [] }; groups.push(cur); }
          cur.members.push(c);
        });
        // Balance groups across THREE vertical columns by member count while preserving dept order.
        // Find split points (i, j) minimising max(col1, col2, col3).
        const counts = groups.map(g => g.members.length);
        const N = groups.length;
        const prefix = [0];
        counts.forEach(c => prefix.push(prefix[prefix.length - 1] + c));
        // Prefer left-first fill when totals tie: `<=` so the last-visited
        // config wins (iteration order goes toward left-heavy at the end).
        let best = { i: 0, j: N, maxLoad: Infinity };
        for (let i = 0; i <= N; i++) {
          for (let j = i; j <= N; j++) {
            const c1 = prefix[i];
            const c2 = prefix[j] - prefix[i];
            const c3 = prefix[N] - prefix[j];
            const m = Math.max(c1, c2, c3);
            if (m <= best.maxLoad) best = { i, j, maxLoad: m };
          }
        }
        const cols = [groups.slice(0, best.i), groups.slice(best.i, best.j), groups.slice(best.j)];
        const linkStyle = { color: "#555", textDecoration: "underline", lineHeight: "1.05" };
        const cellStyle = { ...tdStyle, verticalAlign: "middle", padding: "2.8pt", lineHeight: "1.05" };
        const renderGroup = (g, gi) => (
          <table key={gi} style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), marginBottom: "1.4pt" }}>
            <tbody>
              <tr>
                <td colSpan={3} style={{ ...thStyle, textAlign: "left", background: "#eaeaea" }}>{g.dept.toUpperCase()}</td>
              </tr>
              {g.members.map((c, i) => {
                const phone = (c.phone || "").trim();
                const email = (c.email || "").trim();
                const telHref = phone.replace(/[^0-9+]/g, "");
                return (
                  <tr key={i} style={{ background: i % 2 === 0 ? "#fafafa" : "white" }}>
                    <td style={{ ...cellStyle, width: "30%", fontWeight: "bold" }}>{c.role}</td>
                    <td style={{ ...cellStyle, width: "52%" }}>
                      <div style={{ margin: 0 }}>{c.name}</div>
                      {showPhone && phone && (
                        <div style={{ margin: 0 }}><a href={`tel:${telHref}`} style={linkStyle}>{phone}</a></div>
                      )}
                      {showEmail && email && (
                        <div style={{ margin: 0 }}><a href={`mailto:${email}`} style={linkStyle}>{email}</a></div>
                      )}
                    </td>
                    <td style={{ ...cellStyle, width: "18%", fontWeight: "bold", textAlign: "center" }}>{c.call_time}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        );
        return (
          <>
            <SectionHeader accent={accent}>Crew Call Times</SectionHeader>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <tbody>
                <tr>
                  <td style={{ width: "33.33%", verticalAlign: "top", paddingRight: "1.4pt" }}>{cols[0].map(renderGroup)}</td>
                  <td style={{ width: "33.33%", verticalAlign: "top", paddingLeft: "1.4pt", paddingRight: "1.4pt" }}>{cols[1].map(renderGroup)}</td>
                  <td style={{ width: "33.33%", verticalAlign: "top", paddingLeft: "1.4pt" }}>{cols[2].map(renderGroup)}</td>
                </tr>
              </tbody>
            </table>
          </>
        );
      })()}

      {/* NOTES */}
      {(() => {
        const items = [
          { key: "safety",  label: "SAFETY NOTES",       value: cs?.safety_notes || "" },
          { key: "covid",   label: "COVID-19 PROTOCOLS", value: cs?.covid_protocols || "" },
          { key: "walkie",  label: "WALKIE CHANNELS",    value: cs?.walkie_channels || "" },
          { key: "quote",   label: "QUOTE OF THE DAY",   value: cs?.quote_of_the_day || "", italic: true },
        ].filter(i => (i.value || "").trim());
        if (items.length === 0) return null;

        // Layout rules per the user's spec (based on filled-count):
        //   0 → hide  |  1 → 1 full-width row  |  2 → stacked (current behaviour)
        //   3 → first two side-by-side + third full-width  |  4 → 2×2 grid
        const rows = [];
        if (items.length >= 3) {
          rows.push([items[0], items[1]]);                 // row 1: first two together
          if (items.length === 4) rows.push([items[2], items[3]]);  // row 2: pair
          else rows.push([items[2]]);                      // row 2: third full-width
        } else {
          items.forEach(it => rows.push([it]));            // stacked (1 or 2 filled)
        }

        const cellContentStyle = (italic) => ({ ...tdStyle, whiteSpace: "pre-wrap", ...(italic ? { fontStyle: "italic" } : {}) });
        // Widths — when a row holds two items each label+value takes half; a lone item spans full width
        const twoUpLabelW = "13%";
        const twoUpValueW = "37%";
        const fullLabelW  = "15%";
        return (
          <>
            <SectionHeader accent={accent}>Notes</SectionHeader>
            <table style={{ width: "100%", borderCollapse: "collapse", border: borderRule(), tableLayout: "fixed" }}>
              <tbody>
                {rows.map((row, ri) => (
                  <tr key={ri}>
                    {row.length === 2 ? (
                      <>
                        <td style={{ ...thStyle, width: twoUpLabelW }}>{row[0].label}</td>
                        <td style={{ ...cellContentStyle(row[0].italic), width: twoUpValueW, borderRight: borderRule() }}>{row[0].value}</td>
                        <td style={{ ...thStyle, width: twoUpLabelW }}>{row[1].label}</td>
                        <td style={{ ...cellContentStyle(row[1].italic), width: twoUpValueW }}>{row[1].value}</td>
                      </>
                    ) : (
                      <>
                        <td style={{ ...thStyle, width: fullLabelW }}>{row[0].label}</td>
                        <td style={cellContentStyle(row[0].italic)} colSpan={3}>{row[0].value}</td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        );
      })()}

      {/* FOOTER — pinned to the bottom of the page card */}
      </div>
      <FooterStrip />
        </div>
      )}
    </div>
  );
}
